import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { themeToHex } from '@feathr/components';
import { useUser } from '@feathr/extender/state';
import type { IEmailABTestColor } from '@feathr/extender/utils/emailABTest';

import {
  AB_TEST_CHART_HEIGHT,
  formatABTestChartTick,
  formatABTestChartTooltip,
} from './ABTestReport.utils';

export interface IABTestLine<T> extends IEmailABTestColor {
  dataKey: keyof T & string;
  name: string;
}

interface IProps<T extends { date: string }> {
  data: T[];
  lines: Array<IABTestLine<T>>;
  valueFormatter?: (value: number) => string;
}

function ABTestLineChart<T extends { date: string }>({
  data,
  lines,
  valueFormatter,
}: Readonly<IProps<T>>): JSX.Element {
  const user = useUser();
  const timezone = user.get('timezone');

  function formatTick(date: string): string {
    return formatABTestChartTick(date, timezone);
  }

  function formatTooltipLabel(date: string): string {
    return formatABTestChartTooltip(date, timezone);
  }

  return (
    <ResponsiveContainer height={AB_TEST_CHART_HEIGHT} width={'99%'}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray={'4 4'} vertical={false} />
        <XAxis dataKey={'date'} interval={'preserveStartEnd'} tickFormatter={formatTick} />
        <YAxis tickFormatter={valueFormatter} />
        <Tooltip formatter={valueFormatter} labelFormatter={formatTooltipLabel} />
        <Legend />
        {lines.map(({ dataKey, name, shade, theme }) => (
          <Line
            dataKey={dataKey}
            key={dataKey}
            name={name}
            stroke={themeToHex(theme, shade)}
            type={'linear'}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default observer(ABTestLineChart) as typeof ABTestLineChart;
